import React from 'react';
import { SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import { formatDuration } from './flightData';
import {
  TIME_BUCKETS, STOP_OPTIONS, SORT_OPTIONS, DEFAULT_FILTERS,
  deriveFacets, isFiltered,
} from './flightFilters';

const AIRLINE_COLLAPSE_AT = 8;

const Chip = ({ active, onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
      active
        ? 'bg-sky-500 text-white border-sky-500'
        : 'bg-sky-50 text-sky-700 border-sky-100 hover:border-sky-300'
    }`}
  >
    {children}
  </button>
);

const Group = ({ label, children }) => (
  <div>
    <p className="text-xs text-gray-500 mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const FlightFilters = ({ offers, filters, sortBy, onFiltersChange, onSortChange, matchedCount }) => {
  const [showAllAirlines, setShowAllAirlines] = React.useState(false);
  const facets = React.useMemo(() => deriveFacets(offers), [offers]);
  const filtered = isFiltered(filters);

  const toggleIn = (key, value) => {
    const cur = filters[key];
    onFiltersChange({
      ...filters,
      [key]: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value],
    });
  };

  const [minDur, maxDur] = facets.durationRange || [0, 0];
  const hasDurationSlider = facets.durationRange && maxDur > minDur;
  const durValue = filters.maxDurationMin ?? maxDur;

  const airlines = showAllAirlines ? facets.airlines : facets.airlines.slice(0, AIRLINE_COLLAPSE_AT);

  return (
    <div className="rounded-3xl bg-white/95 border border-sky-100 shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-gray-700 font-bold">
          <SlidersHorizontal className="w-4 h-4 text-sky-500" />
          筛选与排序
          <span className="text-xs font-normal text-gray-400">
            {filtered ? `${matchedCount} / ${offers.length} 个航班` : `共 ${offers.length} 个航班`}
          </span>
        </div>
        {filtered && (
          <button
            type="button"
            onClick={() => onFiltersChange(DEFAULT_FILTERS)}
            className="text-xs text-gray-400 hover:text-sky-600 flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" /> 清除筛选
          </button>
        )}
      </div>

      <div className="space-y-4">
        <Group label="排序">
          {SORT_OPTIONS.map((s) => (
            <Chip key={s.key} active={sortBy === s.key} onClick={() => onSortChange(s.key)}>
              {s.label}
            </Chip>
          ))}
        </Group>

        <Group label="中转">
          {STOP_OPTIONS.map((s) => (
            <Chip
              key={s.key}
              active={filters.stops === s.key}
              onClick={() => onFiltersChange({ ...filters, stops: s.key })}
            >
              {s.label}
              {s.key === 'direct' && <span className="ml-1 opacity-70">{facets.directCount}</span>}
            </Chip>
          ))}
        </Group>

        {(facets.freeRefundCount > 0 || facets.freeChangeCount > 0) && (
          <Group label="退改">
            <Chip
              active={filters.refundFree}
              onClick={() => onFiltersChange({ ...filters, refundFree: !filters.refundFree })}
            >
              可免费退票 <span className="opacity-70">{facets.freeRefundCount}</span>
            </Chip>
            <Chip
              active={filters.changeFree}
              onClick={() => onFiltersChange({ ...filters, changeFree: !filters.changeFree })}
            >
              可免费改期 <span className="opacity-70">{facets.freeChangeCount}</span>
            </Chip>
          </Group>
        )}

        <Group label="起飞时间（出发地当地时间）">
          {TIME_BUCKETS.map((b) => (
            <Chip
              key={b.key}
              active={filters.depBuckets.includes(b.key)}
              onClick={() => toggleIn('depBuckets', b.key)}
              title={b.hint}
            >
              {b.label} <span className="opacity-60">{b.hint}</span>
            </Chip>
          ))}
        </Group>

        <Group label="到达时间（目的地当地时间）">
          {TIME_BUCKETS.map((b) => (
            <Chip
              key={b.key}
              active={filters.arrBuckets.includes(b.key)}
              onClick={() => toggleIn('arrBuckets', b.key)}
              title={b.hint}
            >
              {b.label} <span className="opacity-60">{b.hint}</span>
            </Chip>
          ))}
        </Group>

        {facets.airlines.length > 1 && (
          <Group label="航空公司">
            {airlines.map((a) => (
              <Chip
                key={a.code}
                active={filters.airlines.includes(a.code)}
                onClick={() => toggleIn('airlines', a.code)}
              >
                {a.name} <span className="opacity-70">{a.count}</span>
              </Chip>
            ))}
            {facets.airlines.length > AIRLINE_COLLAPSE_AT && (
              <button
                type="button"
                onClick={() => setShowAllAirlines((v) => !v)}
                className="px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 hover:text-sky-600 flex items-center gap-1"
              >
                {showAllAirlines ? '收起' : `更多 ${facets.airlines.length - AIRLINE_COLLAPSE_AT} 家`}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAllAirlines ? 'rotate-180' : ''}`} />
              </button>
            )}
          </Group>
        )}

        {hasDurationSlider && (
          <div>
            <p className="text-xs text-gray-500 mb-2">
              总耗时不超过 <span className="text-sky-600 font-medium">{formatDuration(durValue)}</span>
            </p>
            <input
              type="range"
              min={minDur}
              max={maxDur}
              step={30}
              value={durValue}
              onChange={(e) => {
                const v = Number(e.target.value);
                onFiltersChange({ ...filters, maxDurationMin: v >= maxDur ? null : v });
              }}
              className="w-full max-w-md accent-sky-500"
            />
            <div className="flex justify-between max-w-md text-[11px] text-gray-400">
              <span>{formatDuration(minDur)}</span>
              <span>{formatDuration(maxDur)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightFilters;
